import axios from 'axios';

import type { UserDTO } from '@hotjar/user';

import type { CommentId, Thread as ThreadDto } from '../dto';

import {
  threadDtoToThreadModel,
  commentModelToCommentDto,
  replyModelToReplyDto,
  threadModelToThreadDto,
} from '../mapper';
import type { CommentsApi } from './types';

type SiteId = string;

/* eslint-disable camelcase */
interface GenericSuccessData {
  success: true;
}

interface CreateResponseData {
  success: true;
  comment_id: CommentId;
  created_epoch_time: number;
}

interface GetResponseData {
  success: true;
  comment: ThreadDto;
  users: Record<UserDTO['id'], Omit<UserDTO, 'id'>>;
}

/* eslint-enable camelcase */

// NOTE: we always want to use secure API, even when the player is loaded over HTTP. Axios automatically sets the
// protocol to https for us.
const API_ROOT = `/api/v1/sites`;

function createEndpointPath(siteId: SiteId): string {
  return `${API_ROOT}/${siteId}/comment`;
}

function createCommentEndpointPath(siteId: SiteId, commentId: CommentId): string {
  return `${createEndpointPath(siteId)}/${commentId}`;
}

export async function getAll({
  siteId,
  scopeId,
  token,
}: Parameters<CommentsApi['getAll']>[0]): ReturnType<CommentsApi['getAll']> {
  if (scopeId === null) {
    return [];
  }

  const queryParams = token === null ? '' : `?token=${token}`;
  const path = createCommentEndpointPath(siteId, scopeId);
  const response = await axios.get<GetResponseData>(path + queryParams);

  return [threadDtoToThreadModel(response.data.users, response.data.comment)];
}

export async function createThread({
  siteId,
  widgetId,
  thread,
}: Parameters<CommentsApi['createThread']>[0]): ReturnType<CommentsApi['createThread']> {
  const path = `${API_ROOT}/${siteId}/highlights/${widgetId}/comment`;
  const response = await axios.post<CreateResponseData>(
    path,
    threadModelToThreadDto({ ...thread, widgetId, replies: [], id: 0, createdEpochTime: 0 }),
  );

  return {
    ...thread,
    id: response.data.comment_id,
    createdEpochTime: response.data.created_epoch_time,
    widgetId,
    replies: [],
  };
}

export async function createReply({
  siteId,
  scopeId,
  reply,
}: Parameters<CommentsApi['createReply']>[0]): ReturnType<CommentsApi['createReply']> {
  if (scopeId === null) {
    throw new Error('Scope ID is mandatory for the highlight');
  }

  const path = createEndpointPath(siteId);
  const response = await axios.post<CreateResponseData>(
    path,
    replyModelToReplyDto({ ...reply, id: 0, createdEpochTime: 0 }),
  );

  return {
    ...reply,
    id: response.data.comment_id,
    createdEpochTime: response.data.created_epoch_time,
  };
}

export async function update({
  siteId,
  comment,
}: Parameters<CommentsApi['update']>[0]): ReturnType<CommentsApi['update']> {
  const path = createCommentEndpointPath(siteId, comment.id);

  await axios.put<GenericSuccessData>(path, commentModelToCommentDto(comment));

  return comment;
}

export async function remove({
  siteId,
  comment,
}: Parameters<CommentsApi['remove']>[0]): ReturnType<CommentsApi['remove']> {
  const path = createCommentEndpointPath(siteId, comment.id);

  await axios.delete<GenericSuccessData>(path);

  return comment;
}
